// Polyfill for React 18+ compatibility with @twilio/video-room-monitor
// MUST be imported before any code that uses the monitor
import './utils/reactDomRenderPolyfill';

import React from 'react';
import { createRoot } from 'react-dom/client';

import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import App from './App';
import AppStateProvider, { useAppState } from './state';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import ErrorDialog from './components/ErrorDialog/ErrorDialog';
import LoginPage from './components/LoginPage/LoginPage';
import TimeoutPage from './components/TimeoutPage/TimeoutPage';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import theme from './theme';
import './types';
import { ChatProvider } from './components/ChatProvider';
import { ParticipantWindowProvider } from './components/ParticipantWindowProvider';
import { ParticipantProvider } from './components/ParticipantProvider';
import { VideoProvider } from './components/VideoProvider';
import useConnectionOptions from './utils/useConnectionOptions/useConnectionOptions';
import UnsupportedBrowserWarning from './components/UnsupportedBrowserWarning/UnsupportedBrowserWarning';

import InvitePage from './components/CallInvite/InvitePage';

import * as Sentry from '@sentry/react';
import { VoiceProvider } from './components/VoiceProvider';
import { socket, SocketContext } from './utils/socket';

declare global {
  interface Window {
    gtag: any;
  }
}

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    environment: process.env.REACT_APP_ENVIRONMENT ? process.env.REACT_APP_ENVIRONMENT : 'production',
    // debug: true
  });
}

const VideoApp = () => {
  const { error, setError } = useAppState();
  const connectionOptions = useConnectionOptions();

  return (
    <VideoProvider options={connectionOptions} onError={setError}>
      <SocketContext.Provider value={socket}>
        <ErrorDialog dismissError={() => setError(null)} error={error} />
        <ParticipantProvider>
          <ChatProvider>
            <VoiceProvider>
              <ParticipantWindowProvider>
                <App />
              </ParticipantWindowProvider>
            </VoiceProvider>
          </ChatProvider>
        </ParticipantProvider>
      </SocketContext.Provider>
    </VideoProvider>
  );
};

export const ReactApp = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <UnsupportedBrowserWarning>
      <Router>
        <AppStateProvider>
          <Routes>
            <Route path="/" element={
              <PrivateRoute>
                <VideoApp />
              </PrivateRoute>
            } />
            <Route path="/meeting/:meetingId" element={
              <PrivateRoute>
                <VideoApp />
              </PrivateRoute>
            } />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/timeout" element={<TimeoutPage />} />
            <Route path="/call/:meetingId" element={<InvitePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppStateProvider>
      </Router>
    </UnsupportedBrowserWarning>
  </ThemeProvider>
);

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(<ReactApp />);
